import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Cropper from 'react-easy-crop';
import ProtectedLayout from '../components/ProtectedLayout';
import api from '../api';
import { useAuth } from '../AuthContext';
import { usePostMessage } from '../PostMessageContext';
import { toast } from 'sonner';
import { getMissingFields } from '../utils/setupCompletion';
import { WarningTriangle, RequiredExclamation } from '../components/StatusIcons';

// ── Option A: Info icon tooltip on field label ──
function Tooltip({ text }) {
  const [visible, setVisible] = React.useState(false);
  const ref = React.useRef(null);

  return (
    <span
      ref={ref}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: '5px', verticalAlign: 'middle' }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <i className="fas fa-circle-info" style={{ color: '#94A3B8', fontSize: '13px', cursor: 'help', transition: 'color 150ms' }}
        onMouseEnter={e => e.target.style.color = '#2D87F0'}
        onMouseLeave={e => e.target.style.color = '#94A3B8'}
      ></i>
      {visible && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%',
          transform: 'translateX(-50%)',
          background: '#0F172A', color: 'white',
          fontSize: '12px', lineHeight: '1.5',
          padding: '8px 12px', borderRadius: '8px',
          width: '240px', zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          pointerEvents: 'none',
        }}>
          {text}
          {/* Arrow */}
          <div style={{
            position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0,
            borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
            borderTop: '5px solid #0F172A',
          }} />
        </div>
      )}
    </span>
  );
}

// ── Field with Option A tooltip icon ──
function FormField({ label, hint, children, required, recommended, error, missingType }) {
  // missingType ('required' | 'recommended') outlines the input to show users
  // exactly which fields are causing the page-level alert. `error` (post-save
  // validation) takes precedence over the missing-field outline.
  const showOutline = !error && (missingType === 'required' || missingType === 'recommended');
  const outlineColor = missingType === 'required' ? '#DC2626' : '#10c0a5';
  // Floating icon badge — absolutely positioned in the input's top-right
  // so the label stays the same width and the field doesn't reflow.
  const renderMissingIconBadge = () => {
    if (error || !missingType) return null;
    return (
      <span
        aria-hidden="false"
        style={{
          position: 'absolute',
          top: '-9px',
          right: '8px',
          background: '#fff',
          padding: '0 4px',
          display: 'inline-flex',
          alignItems: 'center',
          lineHeight: 1,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        {missingType === 'required'
          ? <RequiredExclamation size={12} title="Required field missing" />
          : <WarningTriangle size={14} title="Recommended field missing" />}
      </span>
    );
  };
  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{ display: 'inline-flex', alignItems: 'center', fontWeight: '600', fontSize: '13px', marginBottom: '4px', color: '#0F172A' }}>
        {label}
        {required && (
          <span style={{ 
            fontSize: '11px', 
            fontWeight: '500', 
            color: '#DC2626', 
            marginLeft: '4px' 
          }}>
            (required)
          </span>
        )}
        {recommended && (
          <span style={{ 
            fontSize: '11px', 
            fontWeight: '500', 
            color: '#10c0a5', 
            marginLeft: '4px' 
          }}>
            (recommended)
          </span>
        )}
        {hint && <Tooltip text={hint} />}
      </label>
      {showOutline ? (
        <div
          data-testid={`missing-${missingType}-outline`}
          style={{
            position: 'relative',
            border: `2px solid ${outlineColor}`,
            borderRadius: '10px',
            padding: '2px',
            background: missingType === 'required' ? '#FEF2F2' : '#F0FDFA',
          }}
        >
          {renderMissingIconBadge()}
          {children}
        </div>
      ) : children}
      {error && (
        <p style={{ fontSize: '12px', color: '#DC2626', margin: '4px 0 0', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <i className="fas fa-exclamation-circle" style={{ fontSize: '11px' }}></i>{error}
        </p>
      )}
    </div>
  );
}

// ── Option B: Resource label style KB banner ──
function HelpBanner({ section, title, videoUrl, articleUrl, onPlay, videoOpen }) {
  const storageKey = `qp_help_dismissed_${section}`;
  const [visible, setVisible] = useState(() => !localStorage.getItem(storageKey));

  const dismiss = () => {
    localStorage.setItem(storageKey, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: '16px', marginBottom: '20px',
      padding: '14px 18px', background: 'white',
      border: '1px solid #DDE7F1', borderLeft: '4px solid #2D87F0',
      borderRadius: '10px', boxShadow: '0 1px 4px rgba(15,23,42,0.06)', flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 1, minWidth: '200px' }}>
        <i className="fas fa-graduation-cap" style={{ color: '#2D87F0', fontSize: '20px', marginTop: '2px', flexShrink: 0 }}></i>
        <div>
          <div style={{ fontWeight: '700', fontSize: '14px', color: '#0F172A', marginBottom: '2px' }}>
            Setup Guide: {title}
          </div>
          <div style={{ fontSize: '13px', color: '#64748B' }}>
            Video walkthrough and step-by-step documentation available.
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        {videoUrl && (
          <button
            onClick={onPlay}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: 'white', background: videoOpen ? '#1D6FD8' : '#2D87F0', border: 'none', cursor: 'pointer', padding: '7px 14px', borderRadius: '7px', whiteSpace: 'nowrap', transition: 'background 150ms' }}>
            <i className={`fas ${videoOpen ? 'fa-times' : 'fa-play'}`} style={{ fontSize: '11px' }}></i>
            {videoOpen ? 'Close Video' : 'Watch Video'}
          </button>
        )}
        {articleUrl && (
          <a href={articleUrl} target="_blank" rel="noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#2D87F0', background: '#EFF6FF', border: '1px solid #BFDBFE', textDecoration: 'none', padding: '7px 14px', borderRadius: '7px', whiteSpace: 'nowrap' }}>
            <i className="fas fa-file-lines" style={{ fontSize: '11px' }}></i> Read Article
          </a>
        )}
        <button onClick={dismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '16px', padding: '4px', marginLeft: '4px', lineHeight: 1 }}>
          <i className="fas fa-times"></i>
        </button>
      </div>
    </div>
  );
}

// ── Disclaimer text generators ──
function getDefaultResultsDisclaimer(brand) {
  const name = brand?.title_legal || brand?.title || 'Your Agency';
  const phone = brand?.phone_internal || brand?.phone || '(xxx) xxx-xxxx';
  return `By clicking the button above and submitting your online term life insurance quote request to ${name}, you are agreeing by your electronic signature to give ${name} and its partners, your prior express written consent and continuing established business relationship permission to call you at each cell and residential phone number you provided in your online quote request, and any other subscriber or user of these phone numbers, using an automatic dialing system and pre-recorded and artificial voice messages any time from and after your inquiry to ${name} for purposes of all federal and state telemarketing and Do-Not-Call laws and your prior affirmative written consent to email you at the email address(s) you provided in your online quote request, in each case to market our products and services to you and for all other purposes. Your consent is not required to get a quote or purchase anything from ${name}, and you may instead reach us by phone at ${phone}.`;
}

function getDefaultApplyDisclaimer(brand) {
  const name = brand?.title_legal || brand?.title || 'Your Agency';
  const phone = brand?.phone_internal || brand?.phone || '1 (855) 458-1985';
  return `By clicking the button above and submitting, you agree to our Privacy Policy and consent to be contacted by ${name} via email, phone, or text, including automated technology or pre-recorded messages, as per TCPA and FCC guidelines. Consent is not required to purchase. For alternatives, call ${phone}. Your information remains confidential.`;
}

// ── Auto-fetch logo & favicon from website URL ──
function WebsiteAssetFetcher({ websiteUrl, onUseLogo, onUseFavicon, onColorsExtracted, onTimezoneDetected, logoFilename, faviconFilename, hasExistingData, uploadFileFn, brandId }) {
  const [domain, setDomain] = React.useState('');
  const [logoUrl, setLogoUrl] = React.useState(null);
  const [faviconUrl, setFaviconUrl] = React.useState(null);
  const [logoOk, setLogoOk] = React.useState(false);
  const [faviconOk, setFaviconOk] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [fetched, setFetched] = React.useState(false);
  const [extractedColors, setExtractedColors] = React.useState(null);
  // Individual status tracking
  const [usedLogo, setUsedLogo] = React.useState(false);
  const [usedFavicon, setUsedFavicon] = React.useState(false);
  const [usedColors, setUsedColors] = React.useState(false);
  const [savingLogo, setSavingLogo] = React.useState(false);
  const [savingFavicon, setSavingFavicon] = React.useState(false);
  const [savingAll, setSavingAll] = React.useState(false);

  React.useEffect(() => {
    if (!websiteUrl) { setDomain(''); setFetched(false); return; }
    try {
      const d = new URL(websiteUrl).hostname.replace('www.', '');
      if (d !== domain) { setDomain(d); setFetched(false); resetUsed(); setExtractedColors(null); }
    } catch { setDomain(''); }
  }, [websiteUrl]);

  function resetUsed() { setUsedLogo(false); setUsedFavicon(false); setUsedColors(false); }

  const doFetch = async () => {
    if (!domain) return;
    setLoading(true); setFetched(false); setLogoOk(false); setFaviconOk(false); resetUsed(); setExtractedColors(null);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    // Extract logo
    let foundLogo = false;
    let extractedLogoUrl = null;
    try {
      const res = await fetch(`${BACKEND_URL}/api/settings/extract-logo`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: websiteUrl }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.logo_url) {
          const ok = await verifyImage(data.logo_url);
          if (ok) { extractedLogoUrl = data.logo_url; setLogoUrl(data.logo_url); setLogoOk(true); foundLogo = true; }
        }
      }
    } catch (e) { console.error('Logo extraction failed:', e); }
    if (!foundLogo) {
      for (const src of [`https://www.google.com/s2/favicons?domain=${domain}&sz=256`, `https://icons.duckduckgo.com/ip3/${domain}.ico`]) {
        const ok = await verifyImage(src);
        if (ok) { extractedLogoUrl = src; setLogoUrl(src); setLogoOk(true); foundLogo = true; break; }
      }
    }

    // Favicon
    const favSrc = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
    const favOk = await verifyImage(favSrc);
    if (favOk) { setFaviconUrl(favSrc); setFaviconOk(true); } else { setFaviconOk(false); }

    // Colors — pass extracted logo/favicon URLs for image-based color extraction
    let colors = null;
    try {
      const colorRes = await fetch(`${BACKEND_URL}/api/settings/extract-colors`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: websiteUrl, logo_url: extractedLogoUrl || undefined, favicon_url: favOk ? favSrc : undefined }),
      });
      if (colorRes.ok) {
        const colorData = await colorRes.json();
        if (colorData.success && colorData.colors) { colors = colorData.colors; setExtractedColors(colorData.colors); }
      }
    } catch (e) { /* silent */ }
    if (!colors) { colors = { primary: '#1E3A5F', secondary: '#58A3FF', accent: '#e92027' }; setExtractedColors(colors); }

    // Timezone
    if (onTimezoneDetected) {
      try {
        const tzRes = await fetch(`${BACKEND_URL}/api/settings/detect-timezone`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: websiteUrl }),
        });
        if (tzRes.ok) { const tzData = await tzRes.json(); if (tzData.success && tzData.timezone) onTimezoneDetected(tzData.timezone); }
      } catch (e) { /* silent */ }
    }

    setLoading(false); setFetched(true);
  };

  function verifyImage(src) {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve(img.naturalWidth > 1 && img.naturalHeight > 1);
      img.onerror = () => resolve(false);
      img.src = src;
    });
  }

  const saveRemoteImage = async (url, field) => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const proxyRes = await fetch(`${BACKEND_URL}/api/settings/fetch-remote-image-blob`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });
    if (!proxyRes.ok) throw new Error('Failed to fetch image');
    const blob = await proxyRes.blob();
    const ext = blob.type.includes('png') ? '.png' : blob.type.includes('svg') ? '.svg' : blob.type.includes('ico') ? '.ico' : '.jpg';
    const file = new File([blob], `${field}${ext}`, { type: blob.type });
    const res = await uploadFileFn(file, brandId);
    const data = res.payload?.res || res;
    if (data.success) {
      return data.relative_link || data.filename;
    }
    throw new Error(data.error || 'Upload failed');
  };

  // Individual handlers — save directly without crop
  const handleUseLogo = async () => {
    setSavingLogo(true);
    try {
      const fn = await saveRemoteImage(logoUrl, 'logo');
      onUseLogo(null, fn); setUsedLogo(true);
      toast.success('Logo saved!');
    } catch (e) { toast.error('Error saving logo: ' + e.message); }
    finally { setSavingLogo(false); }
  };
  const handleUseFavicon = async () => {
    setSavingFavicon(true);
    try {
      const fn = await saveRemoteImage(faviconUrl, 'favicon');
      onUseFavicon(null, fn); setUsedFavicon(true);
      toast.success('Favicon saved!');
    } catch (e) { toast.error('Error saving favicon: ' + e.message); }
    finally { setSavingFavicon(false); }
  };

  const handleUseColors = () => {
    if (extractedColors && onColorsExtracted) {
      onColorsExtracted(extractedColors);
      setUsedColors(true);
      toast.success('Colors applied to Website Color Theme!');
    }
  };

  const handleUseAll = async () => {
    setSavingAll(true);
    try {
      if (logoOk && logoUrl && !usedLogo) { setSavingLogo(true); const fn = await saveRemoteImage(logoUrl, 'logo'); onUseLogo(null, fn); setUsedLogo(true); setSavingLogo(false); }
      if (faviconOk && faviconUrl && !usedFavicon) { setSavingFavicon(true); const fn = await saveRemoteImage(faviconUrl, 'favicon'); onUseFavicon(null, fn); setUsedFavicon(true); setSavingFavicon(false); }
      if (extractedColors && onColorsExtracted && !usedColors) { onColorsExtracted(extractedColors); setUsedColors(true); }
      toast.success('All website assets applied!');
    } catch (e) { toast.error('Error applying some assets: ' + e.message); }
    finally { setSavingAll(false); setSavingLogo(false); setSavingFavicon(false); }
  };

  const allUsed = (!logoOk || usedLogo) && (!faviconOk || usedFavicon) && (!extractedColors || usedColors);

  if (!domain) return null;

  const btnStyle = (used, saving) => ({
    height: '26px', padding: '0 10px', fontSize: '11px', fontWeight: '600', border: 'none', borderRadius: '6px', cursor: used || saving ? 'default' : 'pointer', whiteSpace: 'nowrap',
    background: used ? '#16A34A' : '#0284C7', color: 'white', opacity: saving ? 0.7 : 1,
  });

  return (
    <div>
      {/* Pre-fetch: just the button */}
      {!fetched && !loading && (
        <div data-testid="fetch-assets-panel" style={{ padding: '14px 16px', background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '200px' }}>
            <i className="fas fa-globe" style={{ color: '#0284C7', fontSize: '16px' }}></i>
            <div>
              <div style={{ fontWeight: '600', fontSize: '13px', color: '#0C4A6E' }}>Fetch website assets from {domain}</div>
              <div style={{ fontSize: '12px', color: '#0284C7' }}>Import logo, favicon, and primary colors from your website.</div>
            </div>
          </div>
          <button data-testid="fetch-assets-btn" onClick={doFetch} style={{ height: '34px', padding: '0 16px', background: '#0284C7', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
            <i className="fas fa-wand-magic-sparkles" style={{ marginRight: '6px' }}></i>Fetch Website Assets
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ padding: '14px 16px', background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <i className="fas fa-spinner fa-spin" style={{ color: '#0284C7', fontSize: '16px' }}></i>
          <span style={{ fontWeight: '600', fontSize: '13px', color: '#0C4A6E' }}>Fetching assets from {domain}...</span>
        </div>
      )}

      {/* Results panel */}
      {fetched && !loading && (
        <div style={{ border: '1px solid #BAE6FD', borderRadius: '10px', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', background: '#F0F9FF', borderBottom: '1px solid #BAE6FD', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fas fa-check-circle" style={{ color: '#0284C7', fontSize: '14px' }}></i>
              <span style={{ fontWeight: '600', fontSize: '13px', color: '#0C4A6E' }}>Assets found from {domain}</span>
            </div>
            <button data-testid="fetch-try-again" onClick={() => { setFetched(false); resetUsed(); setExtractedColors(null); }} style={{ background: 'none', border: 'none', fontSize: '12px', color: '#0284C7', cursor: 'pointer', padding: '0' }}>
              <i className="fas fa-rotate-right" style={{ marginRight: '4px' }}></i>Re-fetch
            </button>
          </div>
          <div style={{ padding: '16px', background: 'white' }}>
            {/* Asset rows with individual Use buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Logo row */}
              {logoOk && (
                <div data-testid="fetched-logo-row" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                  <div style={{ width: '80px', height: '50px', border: '1px solid #E2E8F0', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', flexShrink: 0 }}>
                    <img src={logoUrl} alt="logo" style={{ maxWidth: '70px', maxHeight: '40px', objectFit: 'contain' }} onError={() => setLogoOk(false)} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A' }}>Company Logo</div>
                    <div style={{ fontSize: '11px', color: '#94A3B8' }}>Click "Use" to apply this logo</div>
                  </div>
                  <button data-testid="use-logo-btn" onClick={handleUseLogo} disabled={usedLogo || savingLogo} style={btnStyle(usedLogo, savingLogo)}>
                    {savingLogo ? <i className="fas fa-spinner fa-spin"></i> : usedLogo ? <><i className="fas fa-check" style={{ marginRight: '4px' }}></i>Applied</> : <><i className="fas fa-download" style={{ marginRight: '4px' }}></i>Use Logo</>}
                  </button>
                </div>
              )}
              {/* Favicon row */}
              {faviconOk && (
                <div data-testid="fetched-favicon-row" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                  <div style={{ width: '50px', height: '50px', border: '1px solid #E2E8F0', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', flexShrink: 0 }}>
                    <img src={faviconUrl} alt="favicon" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A' }}>Favicon</div>
                    <div style={{ fontSize: '11px', color: '#94A3B8' }}>Click "Use" to apply this favicon</div>
                  </div>
                  <button data-testid="use-favicon-btn" onClick={handleUseFavicon} disabled={usedFavicon || savingFavicon} style={btnStyle(usedFavicon, savingFavicon)}>
                    {savingFavicon ? <i className="fas fa-spinner fa-spin"></i> : usedFavicon ? <><i className="fas fa-check" style={{ marginRight: '4px' }}></i>Applied</> : <><i className="fas fa-download" style={{ marginRight: '4px' }}></i>Use Favicon</>}
                  </button>
                </div>
              )}
              {/* Colors row */}
              {extractedColors && (
                <div data-testid="fetched-colors-row" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    {[extractedColors.primary, extractedColors.secondary, extractedColors.accent].filter(Boolean).map((c, i) => (
                      <div key={i} style={{ width: '30px', height: '30px', borderRadius: '6px', border: '1px solid #E2E8F0', backgroundColor: c }} title={c} />
                    ))}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#0F172A' }}>Website Colors</div>
                    <div style={{ fontSize: '11px', color: '#94A3B8' }}>
                      {[extractedColors.primary, extractedColors.secondary, extractedColors.accent].filter(Boolean).join(', ')}
                    </div>
                  </div>
                  <button data-testid="use-colors-btn" onClick={handleUseColors} disabled={usedColors} style={btnStyle(usedColors, false)}>
                    {usedColors ? <><i className="fas fa-check" style={{ marginRight: '4px' }}></i>Applied</> : <><i className="fas fa-palette" style={{ marginRight: '4px' }}></i>Use Colors</>}
                  </button>
                </div>
              )}
            </div>

            {/* No assets found */}
            {!logoOk && !faviconOk && !extractedColors && (
              <div style={{ fontSize: '13px', color: '#64748B', padding: '8px 0' }}>No assets could be extracted from this website.</div>
            )}

            {/* Use all button */}
            {(logoOk || faviconOk || extractedColors) && !allUsed && (
              <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #F1F5F9' }}>
                <button data-testid="use-all-assets-btn" onClick={handleUseAll} disabled={savingAll}
                  style={{ height: '34px', padding: '0 20px', background: '#16A34A', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: savingAll ? 'wait' : 'pointer' }}>
                  {savingAll ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: '6px' }}></i>Applying...</> : <><i className="fas fa-check-double" style={{ marginRight: '6px' }}></i>Use all of the above</>}
                </button>
              </div>
            )}
            {allUsed && (logoOk || faviconOk || extractedColors) && (
              <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="fas fa-check-circle" style={{ color: '#16A34A', fontSize: '14px' }}></i>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#16A34A' }}>All assets applied successfully</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Crop helper: create cropped image blob ──
function getCroppedImg(imageSrc, crop, aspect) {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      // Use fixed output size for known aspects
      if (aspect === 1) {
        canvas.width = 400; canvas.height = 400;
      } else if (Math.abs(aspect - 320/88) < 0.01) {
        canvas.width = 320; canvas.height = 88;
      } else {
        canvas.width = crop.width; canvas.height = crop.height;
      }
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(blob => resolve(blob), 'image/png', 1);
    };
    image.src = imageSrc;
  });
}

// ── Crop Modal ──
function CropModal({ imageSrc, aspect, onComplete, onCancel, title }) {
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState(null);

  const onCropComplete = React.useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    const blob = await getCroppedImg(imageSrc, croppedAreaPixels, aspect);
    onComplete(blob);
  };

  const dimLabel = aspect === 1 ? '400 x 400px' : (aspect && Math.abs(aspect - 320/88) < 0.01) ? '320 x 88px' : null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-testid="crop-modal" style={{ background: 'white', borderRadius: '16px', width: '520px', maxWidth: '95vw', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: '700', fontSize: '15px', color: '#0F172A' }}>{title || 'Crop Image'}</div>
            {dimLabel && <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>Output: {dimLabel}</div>}
          </div>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '18px', padding: '4px' }}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div style={{ position: 'relative', width: '100%', height: '340px', background: '#1E293B' }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <i className="fas fa-search-minus" style={{ color: '#94A3B8', fontSize: '13px' }}></i>
          <input data-testid="crop-zoom-slider" type="range" min={1} max={3} step={0.05} value={zoom} onChange={e => setZoom(Number(e.target.value))} style={{ flex: 1, accentColor: '#2D87F0' }} />
          <i className="fas fa-search-plus" style={{ color: '#94A3B8', fontSize: '13px' }}></i>
        </div>
        <div style={{ padding: '0 20px 16px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ height: '36px', padding: '0 16px', background: 'white', color: '#64748B', border: '1px solid #CBD5E1', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
          <button data-testid="crop-save-btn" onClick={handleSave} style={{ height: '36px', padding: '0 20px', background: '#2D87F0', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
            <i className="fas fa-crop" style={{ marginRight: '6px' }}></i>Apply Crop
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Image upload component with crop ──
function ImageUpload({ label, hint, error, fieldName, currentUrl, onUploaded, onRemoved, required, recommended, missingType, cropAspect, fallbackUrl, uploadFileFn, brandId }) {
  const [uploading, setUploading] = React.useState(false);
  const [preview, setPreview] = React.useState(currentUrl || null);
  const [cropSrc, setCropSrc] = React.useState(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => { setPreview(currentUrl || null); }, [currentUrl]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (cropAspect) {
      // Show crop modal
      const reader = new FileReader();
      reader.onload = () => setCropSrc(reader.result);
      reader.readAsDataURL(file);
    } else {
      uploadFile(file);
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleCropComplete = async (blob) => {
    setCropSrc(null);
    const file = new File([blob], `${fieldName}_cropped.png`, { type: 'image/png' });
    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const res = await uploadFileFn(file, brandId);
      const data = res.payload?.res || res;
      if (data.success) {
        const permalink = data.permalink || `https://static.quoteplicity.com${data.relative_link || ''}`;
        const relativeLink = data.relative_link || data.filename;
        setPreview(permalink + '?t=' + Date.now());
        onUploaded && onUploaded(permalink, relativeLink);
        toast.success(`${label} uploaded successfully!`);
      } else {
        toast.error(data.error || 'Upload failed');
      }
    } catch (err) { console.error(err); toast.error(err.message || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleRemove = async () => {
    try {
      const token = localStorage.getItem('qp_token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${BACKEND_URL}/api/settings/upload/${fieldName}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(null);
      onRemoved && onRemoved();
    } catch (err) { console.error(err); }
  };

  const placeholderIcon = fieldName === 'favicon' ? 'fas fa-star' : fieldName === 'agent_photo' ? 'fas fa-user' : 'fas fa-image';
  const isSquare = fieldName === 'favicon' || fieldName === 'agent_photo';

  return (
    <div>
      {/* Crop modal */}
      {cropSrc && (
        <CropModal
          imageSrc={cropSrc}
          aspect={cropAspect}
          title={`Crop ${label}`}
          onComplete={handleCropComplete}
          onCancel={() => setCropSrc(null)}
        />
      )}

      <div style={{ fontWeight: '600', fontSize: '13px', color: '#0F172A', marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
        {label}
        {required && (
          <span style={{ fontSize: '11px', fontWeight: '500', color: '#DC2626', marginLeft: '4px' }}>(required)</span>
        )}
        {recommended && (
          <span style={{ fontSize: '11px', fontWeight: '500', color: '#10c0a5', marginLeft: '4px' }}>(recommended)</span>
        )}
      </div>

      <div
        data-testid={missingType ? `missing-${missingType}-outline` : undefined}
        onClick={() => !uploading && inputRef.current?.click()}
        style={{
          position: 'relative',
          border: error
            ? '2px dashed #FCA5A5'
            : missingType === 'required'
              ? '2px solid #DC2626'
              : missingType === 'recommended'
                ? '2px solid #10c0a5'
                : `2px dashed ${preview ? '#BFDBFE' : '#DDE7F1'}`,
          borderRadius: '10px', padding: '20px 16px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px',
          background: error
            ? '#FEF2F2'
            : missingType === 'required'
              ? '#FEF2F2'
              : missingType === 'recommended'
                ? '#F0FDFA'
                : preview ? '#EFF6FF' : '#F8FAFC',
          cursor: uploading ? 'default' : 'pointer', transition: 'all 150ms', minHeight: '130px',
        }}
        onMouseEnter={e => { if (!preview && !missingType) e.currentTarget.style.borderColor = '#2D87F0'; }}
        onMouseLeave={e => {
          if (preview || missingType) return;
          e.currentTarget.style.borderColor = '#DDE7F1';
        }}
      >
        {/* Floating missing-field icon — top-right of upload tile, doesn't affect layout */}
        {!error && missingType && (
          <span
            aria-hidden="false"
            style={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              display: 'inline-flex',
              alignItems: 'center',
              lineHeight: 1,
              pointerEvents: 'none',
            }}
          >
            {missingType === 'required'
              ? <RequiredExclamation size={13} title="Required field missing" />
              : <WarningTriangle size={14} title="Recommended field missing" />}
          </span>
        )}
        {preview ? (
          <img src={preview} alt={label} style={{
            maxWidth: isSquare ? '72px' : '140px', maxHeight: '72px',
            objectFit: 'contain', borderRadius: isSquare ? '8px' : '4px',
          }} />
        ) : fallbackUrl ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
            <div style={{
              width: '72px', height: '72px', borderRadius: '8px', overflow: 'hidden',
              background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid #E2E8F0',
            }}>
              <img src={fallbackUrl} alt="Default" style={{
                maxWidth: '56px', maxHeight: '56px', objectFit: 'contain',
              }} />
            </div>
            <span style={{ fontSize: '11px', color: '#94A3B8' }}>Using {fieldName === 'agent_photo' ? 'default' : 'fallback'}</span>
          </div>
        ) : (
          <>
            <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: 'white', border: '1px solid #DDE7F1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className={placeholderIcon} style={{ color: '#94A3B8', fontSize: '20px' }}></i>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: uploading ? '#64748B' : '#2D87F0' }}>
                {uploading ? 'Uploading...' : 'Click to upload'}
              </div>
              <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '2px' }}>PNG, SVG, JPG or WebP</div>
            </div>
          </>
        )}
      </div>

      {preview && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <button type="button" onClick={() => inputRef.current?.click()} className="btn-secondary"
            style={{ flex: 1, height: '32px', fontSize: '12px', padding: '0' }} disabled={uploading}>
            <i className="fas fa-upload" style={{ marginRight: '4px' }}></i>{uploading ? 'Uploading...' : 'Replace'}
          </button>
          <button type="button" onClick={handleRemove}
            style={{ flex: 1, height: '32px', fontSize: '12px', border: '1px solid #FCA5A5', background: 'white', color: '#DC2626', borderRadius: '8px', cursor: 'pointer', padding: '0' }}>
            <i className="fas fa-trash" style={{ marginRight: '4px' }}></i>Remove
          </button>
        </div>
      )}

      {error && <p style={{ fontSize: '12px', color: '#DC2626', margin: '6px 0 0', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}><i className="fas fa-exclamation-circle" style={{ fontSize: '11px' }}></i>{error}</p>}
      {!error && hint && <p style={{ fontSize: '11px', color: '#94A3B8', margin: '6px 0 0', lineHeight: '1.4' }}>{hint}</p>}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
    </div>
  );
}


const SECTION_CONFIG = {
  '/settings/branding': {
    title: 'Customize Branding',
    icon: 'fas fa-palette',
    section: 'general',
    videoUrl: 'https://embed.app.guidde.com/playbooks/kZaGUW7Rv7cHLmnAQ2EVXY?mode=videoOnly',
    articleUrl: 'https://app.modalsupport.com/article/details/CMf8PDYWLmrdhpTepL5E/opqqdrnmxzrVg96jIuym?preview=true',
  },
  '/settings/quoter': {
    title: 'Additional Configuration',
    icon: 'fas fa-filter',
    section: 'quoter',
    videoUrl: 'https://embed.app.guidde.com/playbooks/wfTpqwW5XHjwvVvanLSXXn?mode=videoOnly',
    articleUrl: 'https://app.modalsupport.com/article/details/CMf8PDYWLmrdhpTepL5E/ztWHzELbxQCOH81Hp0Bk?preview=true',
  },
  '/settings/legal': {
    title: 'Disclaimers & Legal',
    icon: 'fas fa-scale-balanced',
    section: 'legal',
    videoUrl: 'https://embed.app.guidde.com/playbooks/hazAcbPopcGLJ1qEjAXxhX?mode=videoOnly',
    articleUrl: 'https://app.modalsupport.com/article/details/CMf8PDYWLmrdhpTepL5E/6FccbN0Oo1pwgx0rP4WT?preview=true',
  },
  '/settings/tracking': {
    title: 'Tracking & Analytics',
    icon: 'fas fa-chart-bar',
    section: 'tracking',
    videoUrl: 'https://embed.app.guidde.com/playbooks/jRVboBAxPgWt2k38i6m3Pe?mode=videoOnly',
    articleUrl: 'https://app.modalsupport.com/article/details/CMf8PDYWLmrdhpTepL5E/Ws9USjRXNQBcSSHcmJ3A?preview=true',
  },
};

function ColorPickerField({ label, hint, value, defaultColor, onApply }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('hex'); // 'hex' or 'rgb'
  const [hue, setHue] = useState(0);
  const [sat, setSat] = useState(100);
  const [light, setLight] = useState(50);
  const [hex, setHex] = useState(value || defaultColor);
  const [rgb, setRgb] = useState({ r: 0, g: 0, b: 0 });
  const current = value || defaultColor;
  const ref = useRef(null);
  const padRef = useRef(null);

  const hexToRgb = (h) => {
    h = h.replace('#', '');
    if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
    return { r: parseInt(h.substring(0,2),16), g: parseInt(h.substring(2,4),16), b: parseInt(h.substring(4,6),16) };
  };

  const rgbToHex = (r, g, b) => '#' + [r,g,b].map(x => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2,'0')).join('');

  const hexToHsl = (h) => {
    const { r, g, b } = hexToRgb(h);
    const r1 = r/255, g1 = g/255, b1 = b/255;
    const max = Math.max(r1,g1,b1), min = Math.min(r1,g1,b1);
    let hu = 0, s = 0, l = (max+min)/2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d/(2-max-min) : d/(max+min);
      if (max === r1) hu = ((g1-b1)/d + (g1 < b1 ? 6 : 0))/6;
      else if (max === g1) hu = ((b1-r1)/d + 2)/6;
      else hu = ((r1-g1)/d + 4)/6;
    }
    return [Math.round(hu*360), Math.round(s*100), Math.round(l*100)];
  };

  const hslToHex = (h, s, l) => {
    s /= 100; l /= 100;
    const f = (n) => { const k = (n + h/30) % 12; const a = s * Math.min(l, 1-l); return Math.round(255 * (l - a * Math.max(-1, Math.min(k-3, 9-k, 1)))); };
    return '#' + [f(0),f(8),f(4)].map(x => x.toString(16).padStart(2,'0')).join('');
  };

  const syncAll = (hexVal) => {
    setHex(hexVal);
    setRgb(hexToRgb(hexVal));
    const [h, s, l] = hexToHsl(hexVal);
    setHue(h); setSat(s); setLight(l);
  };

  const openPicker = () => { syncAll(current); setOpen(true); };

  const handlePadClick = (e) => {
    const rect = padRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    const newS = Math.round(x * 100), newL = Math.round(100 - y * 100);
    setHue(hue); setSat(newS); setLight(newL);
    const h = hslToHex(hue, newS, newL);
    setHex(h); setRgb(hexToRgb(h));
  };

  const handleHueChange = (h) => {
    setHue(h);
    const hx = hslToHex(h, sat, light);
    setHex(hx); setRgb(hexToRgb(hx));
  };

  const handleHexInput = (v) => {
    setHex(v);
    const clean = v.trim().replace('#','');
    if (/^[0-9a-f]{6}$/i.test(clean)) {
      const full = '#' + clean;
      setRgb(hexToRgb(full));
      const [h, s, l] = hexToHsl(full);
      setHue(h); setSat(s); setLight(l);
    }
  };

  const handleRgbInput = (channel, val) => {
    const n = val === '' ? 0 : Math.max(0, Math.min(255, parseInt(val) || 0));
    const newRgb = { ...rgb, [channel]: n };
    setRgb(newRgb);
    const hx = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
    setHex(hx);
    const [h, s, l] = hexToHsl(hx);
    setHue(h); setSat(s); setLight(l);
  };

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const tabStyle = (active) => ({
    flex: 1, padding: '4px 0', borderRadius: '4px', border: 'none', fontSize: '11px', fontWeight: '600',
    cursor: 'pointer', background: active ? '#2D87F0' : 'transparent', color: active ? '#fff' : '#64748B',
    transition: 'all 0.15s',
  });

  return (
    <FormField label={label} hint={hint}>
      <div style={{ position: 'relative' }} ref={ref}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div onClick={openPicker}
            style={{ width: '40px', height: '36px', borderRadius: '6px', border: '1px solid #CBD5E1', cursor: 'pointer', backgroundColor: current, flexShrink: 0 }} />
          <input className="input-field" type="text" value={current} readOnly
            onClick={openPicker}
            placeholder={defaultColor} style={{ flex: 1, fontFamily: 'monospace', cursor: 'pointer' }} />
        </div>
        {open && (
          <div style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: '6px', zIndex: 50, background: '#fff', borderRadius: '10px', boxShadow: '0 8px 30px rgba(0,0,0,0.18)', padding: '12px', width: '240px' }}>
            <div ref={padRef} onClick={handlePadClick}
              style={{ width: '100%', height: '130px', borderRadius: '6px', cursor: 'crosshair', position: 'relative',
                background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #808080, hsl(${hue},100%,50%))` }}>
              <div style={{ position: 'absolute', left: `${sat}%`, top: `${100 - light}%`, width: '12px', height: '12px', borderRadius: '50%', border: '2px solid #fff', boxShadow: '0 0 3px rgba(0,0,0,0.5)', transform: 'translate(-50%,-50%)', pointerEvents: 'none' }} />
            </div>
            <input type="range" min="0" max="360" value={hue} onChange={e => handleHueChange(Number(e.target.value))}
              style={{ width: '100%', marginTop: '10px', height: '14px', borderRadius: '7px', appearance: 'none', cursor: 'pointer',
                background: 'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)' }} />
            <div style={{ display: 'flex', background: '#F1F5F9', borderRadius: '6px', padding: '2px', marginTop: '10px' }}>
              <button onClick={() => setMode('hex')} style={tabStyle(mode === 'hex')}>HEX</button>
              <button onClick={() => setMode('rgb')} style={tabStyle(mode === 'rgb')}>RGB</button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid #CBD5E1', backgroundColor: hex, flexShrink: 0 }} />
              {mode === 'hex' ? (
                <input className="input-field" type="text" value={hex} onChange={e => handleHexInput(e.target.value)}
                  placeholder="#000000" style={{ flex: 1, fontFamily: 'monospace', fontSize: '13px', textAlign: 'center', padding: '4px 8px' }} />
              ) : (
                <div style={{ display: 'flex', gap: '4px', flex: 1 }}>
                  {['r','g','b'].map(ch => (
                    <div key={ch} style={{ flex: 1, textAlign: 'center' }}>
                      <input className="input-field" type="number" min="0" max="255" value={rgb[ch]}
                        onChange={e => handleRgbInput(ch, e.target.value)}
                        style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px', textAlign: 'center', padding: '4px 2px' }} />
                      <div style={{ fontSize: '9px', color: '#94A3B8', fontWeight: '600', marginTop: '2px' }}>{ch.toUpperCase()}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
              <button onClick={() => setOpen(false)}
                style={{ flex: 1, padding: '7px 0', borderRadius: '6px', border: '1px solid #CBD5E1', background: '#fff', fontSize: '12px', fontWeight: '600', color: '#64748B', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={() => { onApply(hex.startsWith('#') ? hex : '#' + hex); setOpen(false); }}
                style={{ flex: 1, padding: '7px 0', borderRadius: '6px', border: 'none', background: '#2D87F0', fontSize: '12px', fontWeight: '600', color: '#fff', cursor: 'pointer' }}>
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    </FormField>
  );
}

export default function BrandingPage() {
  const location = useLocation();
  const { setBrand: setAuthBrand, brand: authBrand } = useAuth();
  const { saveBrand, uploadFile: uploadFileFn, sendToParent, registerHandler } = usePostMessage();
  const config = SECTION_CONFIG[location.pathname] || SECTION_CONFIG['/settings/branding'];

  const [brand, setBrand] = useState(null);
  const [originalBrand, setOriginalBrand] = useState(null);
  const timezones = {
    'UTC': 'UTC',
    'America/New_York': 'Eastern',
    'America/Chicago': 'Central',
    'America/Denver': 'Mountain',
    'America/Phoenix': 'Mountain, no DST',
    'America/Los_Angeles': 'Pacific',
    'America/Anchorage': 'Alaska',
    'America/Adak': 'Hawaii',
    'Pacific/Honolulu': 'Hawaii, no DST',
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState({});
  const [subdomainManuallyEdited, setSubdomainManuallyEdited] = useState(false);
  const [showSubdomainWarning, setShowSubdomainWarning] = useState(false);
  const [subdomainPendingValue, setSubdomainPendingValue] = useState('');
  const [subdomainChangeConfirmed, setSubdomainChangeConfirmed] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [domainState, setDomainState] = useState(0); // 0=idle, 1=applying, 2=complete, 3=failed
  const [domainMessage, setDomainMessage] = useState('');
  const [domainInput, setDomainInput] = useState(''); // local-only, not in save payload

  const brandInitialized = useRef(false);
  const savedScriptsRef = useRef({ status: 0, current_value: '', requested_value: '' });

  // Listen for quoter_domain_status from parent
  useEffect(() => {
    const unregister = registerHandler('quoter_domain_status', (data) => {
      const payload = data.payload || data;
      const state = payload.customDomainState ?? domainState;
      setDomainState(state);
      if (payload.message) {
        const domain = domainInput;
        setDomainMessage(payload.message.replace('DOMAIN', `<a href="https://${domain}" target="_blank" style="color:#2D87F0;text-decoration:underline">${domain}</a>`));
      }
      if (payload.error) setDomainMessage(payload.error);
    });
    return unregister;
  }, [registerHandler, domainInput]);

  useEffect(() => {
    if (!authBrand || brandInitialized.current) return;
    brandInitialized.current = true;
    try {
      const b = JSON.parse(JSON.stringify(authBrand));
      if (!b.details || Array.isArray(b.details)) b.details = {};
      if (!b.details.tracking || Array.isArray(b.details.tracking)) b.details.tracking = {};
      if (!b.details.legal_links || Array.isArray(b.details.legal_links)) b.details.legal_links = {};
      if (!b.details.results_page || Array.isArray(b.details.results_page)) b.details.results_page = {};
      if (!b.details.apply_page || Array.isArray(b.details.apply_page)) b.details.apply_page = {};
      if (!b.details.agent || Array.isArray(b.details.agent)) b.details.agent = {};
      if (!b.details.theme || Array.isArray(b.details.theme)) b.details.theme = {};
      // Migrate colors -> theme if theme is empty but colors exists
      if (b.details.colors && typeof b.details.colors === 'object' && !Array.isArray(b.details.colors)) {
        if (!b.details.theme.primary && b.details.colors.primary) b.details.theme.primary = b.details.colors.primary;
        if (!b.details.theme.secondary && b.details.colors.secondary) b.details.theme.secondary = b.details.colors.secondary;
        if (!b.details.theme.accent && b.details.colors.accent) b.details.theme.accent = b.details.colors.accent;
        if (!b.details.theme.cta && b.details.colors.cta) b.details.theme.cta = b.details.colors.cta;
      }
      if (!b.details.apply_page.custom_jumpstart_cta || Array.isArray(b.details.apply_page.custom_jumpstart_cta)) b.details.apply_page.custom_jumpstart_cta = {};
      // Normalize custom_scripts: migrate legacy string to object format
      const cs = b.details.custom_scripts;
      if (!cs || typeof cs === 'string' || Array.isArray(cs)) {
        b.details.custom_scripts = { status: 0, current_value: (typeof cs === 'string' ? cs : ''), requested_value: '' };
      }
      if (!b.timezone) {
        b.timezone = 'America/Denver';
      }
      // Seed subdomain_alias from subdomain on init if subdomain_alias is not present in the data
      if (b.subdomain_alias === undefined && b.subdomain) {
        b.subdomain_alias = b.subdomain;
      }
      if (!b.details.results_disclaimer) {
        b.details.results_disclaimer = getDefaultResultsDisclaimer(b);
      }
      if (!b.details.legal_disclaimer) {
        b.details.legal_disclaimer = 'Rates are provided directly by the insurance companies, and we make every effort to keep them up to date. However, pricing can change frequently, and the results shown should be considered estimates. Your actual premium may vary based on factors such as age, health, medications, and underwriting.';
      }
      if (!b.details.apply_disclaimer) {
        b.details.apply_disclaimer = getDefaultApplyDisclaimer(b);
      }
      setBrand(b);
      setDomainInput(b.custom_domain || '');
      setOriginalBrand(JSON.stringify(b));
      savedScriptsRef.current = b.details?.custom_scripts ? { ...b.details.custom_scripts } : { status: 0, current_value: '', requested_value: '' };
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authBrand]);

  useEffect(() => { setIsDirty(false); }, [location.pathname]);

  const updateBrand = (path, value) => {
    setBrand(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let obj = updated;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!obj[parts[i]] || Array.isArray(obj[parts[i]])) obj[parts[i]] = {};
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return updated;
    });
    setIsDirty(true);
  };

  // Generate subdomain from website URL
  const generateSubdomain = (url) => {
    if (!url) return '';
    try {
      // Remove protocol
      let domain = url.replace(/^https?:\/\//i, '');
      // Remove www.
      domain = domain.replace(/^www\./i, '');
      // Remove everything after first slash
      domain = domain.split('/')[0];
      // Remove TLD (.com, .net, .org, .co.uk, etc.)
      domain = domain.replace(/\.(com|net|org|edu|gov|mil|co\.uk|co|io|ai|app|dev|tech)$/i, '');
      // Convert to lowercase and remove any remaining dots or special chars
      domain = domain.toLowerCase().replace(/[^a-z0-9-]/g, '');
      return domain;
    } catch {
      return '';
    }
  };

  const validate = () => {
    const errs = {};
    if (config.section === 'general') {
      if (!brand?.title?.trim())                     errs.title       = 'Agency name is required.';
      if (!brand?.email?.trim()) {
        errs.email = 'Agency email is required.';
      } else if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(brand.email.trim())) {
        errs.email = 'Please enter a valid email address (e.g. name@domain.com)';
      }
      if (!brand?.phone?.trim()) {
        errs.phone = 'Agency phone number is required.';
      } else {
        const digits = brand.phone.replace(/\D/g, '');
        if (digits.length < 10) {
          errs.phone = 'Phone number must be 10 digits: (xxx) xxx-xxxx';
        } else if ('1234567890'.includes(digits) || '0987654321'.includes(digits) || /^(.)\1{9}$/.test(digits)) {
          errs.phone = 'Please enter a valid phone number (no sequential or repeated digits)';
        }
      }
      if (!brand?.website_url?.trim()) {
        errs.website_url = 'Website home page is required.';
      } else {
        try {
          const hostname = new URL(brand.website_url.trim().match(/^https?:\/\//i) ? brand.website_url.trim() : 'https://' + brand.website_url.trim()).hostname;
          if (!/\.[a-zA-Z]{2,}$/.test(hostname)) {
            errs.website_url = 'Please enter a valid URL with a domain extension (e.g. .com, .net, .org)';
          }
        } catch {
          errs.website_url = 'Please enter a valid URL (e.g. https://youragency.com)';
        }
      }
      // Subdomain validation: only apply the length rules when the user has
      // actually changed the value. Some legacy brands have subdomains under
      // 8 characters; we shouldn't block them from saving other fields.
      const currentSub = (brand?.subdomain_alias || '').trim();
      const original = originalBrand ? JSON.parse(originalBrand) : {};
      const originalSub = (original.subdomain_alias || original.subdomain || '').trim();
      const subdomainChanged = currentSub !== originalSub;
      if (!currentSub) {
        errs.subdomain = 'Quoteplicity subdomain is required.';
      } else if (subdomainChanged && currentSub.length < 8) {
        errs.subdomain = 'Subdomain must be at least 8 characters.';
      } else if (subdomainChanged && currentSub.length > 40) {
        errs.subdomain = 'Subdomain must be 40 characters or fewer.';
      }
      if (!brand?.timezone?.trim())                  errs.timezone    = 'Account timezone is required.';
      if (!brand?.logo_filename)                     errs.logo        = 'Company logo is required.';
    }
    if (config.section === 'legal') {
      if (!brand?.details?.legal_links?.privacy_policy?.trim())
        errs.privacy_policy = 'Privacy policy URL is required.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Compute setup_status for parent frame navigation (0=complete, 1=required ok but recommended missing, 2=incomplete)
  const computeSetupStatus = (b) => {
    const score = (requiredOk, recommendedOk) => requiredOk ? (recommendedOk ? 0 : 1) : 2;

    const branding = score(
      [b.title?.trim(), b.email?.trim(), b.phone?.trim(), b.website_url?.trim(),
       b.subdomain_alias?.trim(), b.timezone?.trim(), b.logo_filename].every(Boolean),
      [b.details?.agent?.name?.trim(), b.favicon_filename, b.details?.agent?.filename].every(Boolean)
    );

    const legal = score(
      [b.details?.legal_links?.privacy_policy?.trim()].every(Boolean),
      [b.title_legal?.trim(), b.email_internal?.trim(), b.phone_internal?.trim()].every(Boolean)
    );

    const tracking = score(
      true,
      [b.details?.tracking?.ga_id?.trim()].every(Boolean)
    );

    const planId = b.plan_id || b.brand_plan_id;
    const isBasicOrStarter = planId === 1 || planId === 7 || planId === '1' || planId === '7';
    const quoter = score(
      true,
      isBasicOrStarter || !!domainInput.trim()
    );

    const summary = Math.max(branding, legal, tracking, quoter);

    return { summary, branding, legal, tracking, quoter };
  };

  const handleSave = async () => {
    const isValid = validate();

    setSaving(true);
    setSubdomainChangeConfirmed(false);

    // Check if subdomain changed — show warning before saving.
    // This block is placed AFTER setSaving(true) so the button always provides
    // visible feedback (spinner) even if the modal ends up hidden by parent CSS.
    // Trim both sides to defend against accidental whitespace drift.
    const origBrand = originalBrand ? JSON.parse(originalBrand) : {};
    const origSub = (origBrand.subdomain_alias || origBrand.subdomain || '').trim();
    const newSub = (brand?.subdomain_alias || '').trim();
    if (origSub && newSub && newSub !== origSub && !subdomainChangeConfirmed) {
      setSubdomainPendingValue(newSub);
      setShowSubdomainWarning(true);
      setSaving(false);
      return;
    }

    // Compute setup_status and attach to details
    const detailsWithStatus = { ...brand.details, setup_status: computeSetupStatus(brand) };

    // Set custom_scripts status based on requested_value
    const cs = detailsWithStatus.custom_scripts;
    if (cs && (cs.requested_value ?? '') !== (cs.current_value ?? '')) {
      const newStatus = (cs.requested_value ?? '').trim() === '' ? 0 : 2;
      detailsWithStatus.custom_scripts = { ...cs, status: newStatus };
    }

    const payload = {
      id: brand.id || brand._id || brand.brand_id,
      title: brand.title, title_legal: brand.title_legal,
      email: brand.email, email_internal: brand.email_internal,
      phone: brand.phone, phone_internal: brand.phone_internal,
      website_url: brand.website_url, subdomain_alias: brand.subdomain_alias, subdomain: brand.subdomain_alias,
      timezone: brand.timezone,
      logo_filename: brand.logo_filename,
      favicon_filename: brand.favicon_filename,
      details: detailsWithStatus,
    };

    try {
      const res = await saveBrand(payload);
      const success = res.payload?.res?.success ?? res.payload?.success ?? res.success ?? (res.type === 'save_res');

      if (!success) {
        toast.error('Save failed — parent reported an error.');
        return;
      }

      // Save succeeded — update local brand with computed statuses
      const savedBrand = { ...brand, details: detailsWithStatus, subdomain: brand.subdomain_alias };
      setOriginalBrand(JSON.stringify(savedBrand));
      savedScriptsRef.current = detailsWithStatus.custom_scripts ? { ...detailsWithStatus.custom_scripts } : { status: 0, current_value: '', requested_value: '' };
      setBrand(savedBrand);
      setAuthBrand(savedBrand);
      localStorage.setItem('qp_brand', JSON.stringify(savedBrand));
      setIsDirty(false);

      if (isValid) {
        toast.success('Settings saved successfully!');
      } else {
        toast.success('Settings saved. Some required fields are still incomplete.', { duration: 4000 });
      }
    } catch (e) {
      console.error('Save error:', e?.response?.status, e?.response?.data, e?.message);
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(detail || 'Error saving settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (originalBrand) { setBrand(JSON.parse(originalBrand)); setIsDirty(false); }
  };

  if (loading) {
    return (
      <ProtectedLayout>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px 16px' }}>
          {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: '56px', marginBottom: '12px', borderRadius: '8px' }}></div>)}
        </div>
      </ProtectedLayout>
    );
  }

  // Compute missing-field map per section to drive red/teal field outlines
  // (mirrors the sidebar alert state, surfaced inline on the page itself).
  const missingMap = getMissingFields(brand, domainInput);
  const missing = missingMap[config.section === 'general' ? 'branding' : config.section] || {};

  return (
    <ProtectedLayout>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px 16px', paddingBottom: isDirty ? '100px' : '24px' }}>

        {/* Page header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className={config.icon} style={{ color: '#2D87F0', fontSize: '18px' }}></i>
          </div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#0F172A', margin: 0 }}>{config.title}</h1>
        </div>

        {/* KB banner */}
        <HelpBanner
          section={config.section}
          title={config.title}
          videoUrl={config.videoUrl}
          articleUrl={config.articleUrl}
          onPlay={() => setShowVideo(v => !v)}
          videoOpen={showVideo}
        />

        {/* Video panel — stacked above the form, no horizontal overflow */}
        {showVideo && config.videoUrl && (
          <div style={{ marginBottom: '20px' }} className="portal-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #DDE7F1', background: '#F8FAFC' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <i className="fas fa-play-circle" style={{ color: '#2D87F0', fontSize: '16px' }}></i>
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#0F172A' }}>Setup Guide: {config.title}</span>
              </div>
              <button onClick={() => setShowVideo(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '16px' }}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
              <iframe
                src={config.videoUrl}
                title={`Setup Guide: ${config.title}`}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', borderRadius: '10px' }}
                referrerPolicy="unsafe-url"
                allow="clipboard-write"
                sandbox="allow-popups allow-popups-to-escape-sandbox allow-scripts allow-forms allow-same-origin allow-presentation"
                allowFullScreen
              />
            </div>
          </div>
        )}

        {/* Side-by-side layout when video is open */}
        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', minWidth: 0 }}>

          {/* Form card */}
          <div style={{ flex: 1, minWidth: 0, maxWidth: showVideo ? 'none' : '800px' }}>
            <div className="portal-card" style={{ padding: '28px' }}>

          {/* ── GENERAL ── */}
          {config.section === 'general' && (
            <div>
              {/* Website home page — full width at top */}
              <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #DDE7F1' }}>
                <FormField label="Website home page" required
                  error={errors.website_url}
                  missingType={missing.website_url}
                  hint="Used to link back to your website from the logo and other places.">
                  <input 
                    className="input-field" 
                    type="url" 
                    value={brand?.website_url || ''} 
                    onChange={e => { 
                      let url = e.target.value;
                      updateBrand('website_url', url);
                      
                      // Auto-generate subdomain alias
                      const normalizedUrl = url.trim().match(/^https?:\/\//i) ? url.trim() : 'https://' + url.trim();
                      const newSubdomain = generateSubdomain(normalizedUrl);
                      const currentAlias = brand?.subdomain_alias || '';
                      const oldSubdomain = generateSubdomain(brand?.website_url || '');
                      
                      if (!currentAlias || currentAlias === oldSubdomain) {
                        updateBrand('subdomain_alias', newSubdomain);
                      }
                      
                      setErrors(p => ({...p, website_url: ''})); 
                    }} 
                    onBlur={e => {
                      let url = e.target.value.trim();
                      if (url && !url.match(/^https?:\/\//i)) {
                        url = 'https://' + url;
                        updateBrand('website_url', url);
                      }
                      // Validate domain has a proper TLD
                      if (url) {
                        try {
                          const hostname = new URL(url).hostname;
                          if (!/\.[a-zA-Z]{2,}$/.test(hostname)) {
                            setErrors(p => ({...p, website_url: 'Please enter a valid URL with a domain extension (e.g. .com, .net, .org)'}));
                          }
                        } catch {
                          setErrors(p => ({...p, website_url: 'Please enter a valid URL (e.g. https://youragency.com)'}));
                        }
                      }
                      // If subdomain alias doesn't match new URL, prompt to update
                      if (url && (brand?.subdomain_alias || brand?.subdomain)) {
                        const newSubdomain = generateSubdomain(url);
                        const currentAlias = brand?.subdomain_alias || brand?.subdomain || '';
                        if (newSubdomain && currentAlias !== newSubdomain && subdomainManuallyEdited) {
                          if (window.confirm(`Your Quoteplicity subdomain is currently "${currentAlias}". Would you like to update it to "${newSubdomain}" to match your new website?`)) {
                            updateBrand('subdomain_alias', newSubdomain);
                          }
                        }
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.target.blur();
                        // Focus the next input (Agency name)
                        const next = document.querySelector('[data-field="title"]');
                        if (next) next.focus();
                      }
                    }}
                    placeholder="https://youragency.com" 
                    style={errors.website_url ? { borderColor: '#DC2626' } : {}} 
                  />
                </FormField>
                <WebsiteAssetFetcher
                  websiteUrl={brand?.website_url || ''}
                  onUseLogo={(url, filename) => updateBrand('logo_filename', filename)}
                  onUseFavicon={(url, filename) => updateBrand('favicon_filename', filename)}
                  onColorsExtracted={(colors) => {
                    if (colors.primary) updateBrand('details.theme.primary', colors.primary);
                    if (colors.secondary) updateBrand('details.theme.secondary', colors.secondary);
                    if (colors.accent) updateBrand('details.theme.accent', colors.accent);
                  }}
                  onTimezoneDetected={(tz) => {
                    if (!brand?.timezone || brand.timezone === 'America/Denver') {
                      updateBrand('timezone', tz);
                    }
                  }}
                  logoFilename={brand?.logo_filename}
                  faviconFilename={brand?.favicon_filename}
                  hasExistingData={!!(brand?.logo_filename || brand?.favicon_filename || brand?.details?.theme?.primary)}
                  uploadFileFn={uploadFileFn}
                  brandId={brand?.id || brand?._id || brand?.brand_id}
                />
              </div>

              {/* Two-column fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
              <FormField label="Agency name" required
                  error={errors.title}
                  missingType={missing.title}
                hint="Shown to customers on quote pages and emails. Use your DBA or trade name — the name they'd recognize.">
                <input className="input-field" data-field="title" type="text" value={brand?.title || ''} onChange={e => { updateBrand('title', e.target.value); setErrors(p => ({...p, title: ''})); }} 
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const next = document.querySelector('[data-field="email"]'); if (next) next.focus(); } }}
                  placeholder="e.g. Smith Life Insurance" style={errors.title ? { borderColor: '#DC2626' } : {}} />
              </FormField>
              <FormField label="Agency email" required
                  error={errors.email}
                  missingType={missing.email}
                hint="The email address that displays on all of the results and funnel pages.">
                <input className="input-field" data-field="email" type="email" value={brand?.email || ''} onChange={e => { 
                  updateBrand('email', e.target.value); 
                  setErrors(p => ({...p, email: ''})); 
                }} 
                onBlur={e => {
                  const val = e.target.value.trim();
                  if (val && !/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(val)) {
                    setErrors(p => ({...p, email: 'Please enter a valid email address (e.g. name@domain.com)'}));
                  }
                }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const next = document.querySelector('[data-field="phone"]'); if (next) next.focus(); } }}
                placeholder="hello@youragency.com" style={errors.email ? { borderColor: '#DC2626' } : {}} />
              </FormField>
              <FormField label="Agency phone number" required
                  error={errors.phone}
                  missingType={missing.phone}
                hint="The phone number that displays on all of the funnels and results pages.">
                <input className="input-field" data-field="phone" type="tel" value={brand?.phone || ''} onChange={e => { 
                  // Auto-format phone as (xxx) xxx-xxxx
                  let digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                  let formatted = '';
                  if (digits.length > 0) formatted += '(' + digits.slice(0, 3);
                  if (digits.length >= 3) formatted += ') ';
                  if (digits.length > 3) formatted += digits.slice(3, 6);
                  if (digits.length >= 6) formatted += '-';
                  if (digits.length > 6) formatted += digits.slice(6, 10);
                  updateBrand('phone', formatted); 
                  setErrors(p => ({...p, phone: ''})); 
                }}
                onBlur={e => {
                  const digits = (e.target.value || '').replace(/\D/g, '');
                  if (digits.length > 0 && digits.length < 10) {
                    setErrors(p => ({...p, phone: 'Phone number must be 10 digits: (xxx) xxx-xxxx'}));
                  } else if (digits.length === 10) {
                    const isSequential = '1234567890'.includes(digits) || '0987654321'.includes(digits);
                    const isRepeated = /^(.)\1{9}$/.test(digits);
                    if (isSequential || isRepeated) {
                      setErrors(p => ({...p, phone: 'Please enter a valid phone number (no sequential or repeated digits)'}));
                    }
                  }
                }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const next = document.querySelector('[data-field="timezone"]'); if (next) next.focus(); } }}
                placeholder="(555) 123-4567" style={errors.phone ? { borderColor: '#DC2626' } : {}} />
              </FormField>
              <FormField label="Account timezone" required
                  error={errors.timezone}
                  missingType={missing.timezone}
                hint="Used to timestamp all leads, quote activity, and scheduled reports. Defaulted to your local timezone.">
                <select className="input-field" data-field="timezone" value={brand?.timezone || 'America/Denver'} onChange={e => { updateBrand('timezone', e.target.value); setErrors(p => ({...p, timezone: ''})); }} style={{ appearance: 'none', cursor: 'pointer' }}>
                  {Object.entries(timezones).map(([key, label]) => (
                    <option key={key} value={key}>{key} ({label})</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Default agent name" recommended
                missingType={missing.agent_name}
                hint="If left blank, the agency name will be used.">
                <input className="input-field" type="text" value={brand?.details?.agent?.name || ''} onChange={e => updateBrand('details.agent.name', e.target.value)} placeholder="e.g. John Smith" />
              </FormField>

              {/* ── Upload fields — bottom, full width ── */}
              <div style={{ gridColumn: '1 / -1', marginTop: '8px', paddingTop: '24px', borderTop: '1px solid #DDE7F1' }}>

                <FormField label="Quoteplicity subdomain" required
                  error={errors.subdomain}
                  missingType={missing.subdomain}
                  hint="Lowercase letters, numbers, and hyphens only. Must be between 8–40 characters. This is the domain for your results pages and funnels.">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '15px', color: '#64748B', fontWeight: '500' }}>https://</span>
                    <input 
                      className="input-field" 
                      type="text" 
                      value={brand?.subdomain_alias || ''} 
                      maxLength={40}
                      onChange={e => { 
                        const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                        updateBrand('subdomain_alias', cleaned);
                        setSubdomainManuallyEdited(true);
                        setErrors(p => ({...p, subdomain: ''})); 
                      }} 
                      placeholder="companyname" 
                      style={{
                        ...(errors.subdomain ? { borderColor: '#DC2626' } : {}),
                        flex: 1
                      }} 
                    />
                    <span style={{ fontSize: '15px', color: '#64748B', fontWeight: '500' }}>.quoteplicity.com</span>
                  </div>
                </FormField>

                {/* Subdomain change warning modal */}
                {showSubdomainWarning && (
                  <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)' }} onClick={() => setShowSubdomainWarning(false)}>
                    <div style={{ background: 'white', borderRadius: '12px', padding: '28px', maxWidth: '480px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className="fas fa-exclamation-triangle" style={{ color: '#DC2626', fontSize: '18px' }}></i>
                        </div>
                        <div style={{ fontWeight: '700', fontSize: '16px', color: '#0F172A' }}>Change Subdomain?</div>
                      </div>
                      <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', marginBottom: '10px' }}>
                        Changing your Quoteplicity subdomain will <strong>change the URL of all your results pages, funnels, and embedded widgets.</strong> Any existing links, bookmarks, or integrations using the current subdomain will stop working.
                      </div>
                      <div style={{ fontSize: '13px', color: '#94A3B8', lineHeight: '1.5', marginBottom: '20px', padding: '10px 12px', background: '#F8FAFC', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
                        <i className="fas fa-info-circle" style={{ marginRight: '6px', fontSize: '12px' }}></i>
                        Please make sure to update any external links or integrations that reference your current subdomain.
                      </div>
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button onClick={() => { setShowSubdomainWarning(false); setSubdomainPendingValue(''); }} style={{ height: '36px', padding: '0 16px', borderRadius: '8px', border: '1px solid #DDE7F1', background: 'white', fontSize: '13px', fontWeight: '600', color: '#64748B', cursor: 'pointer' }}>Cancel</button>
                        <button onClick={() => { 
                          setShowSubdomainWarning(false);
                          setSubdomainChangeConfirmed(true);
                          setSubdomainPendingValue('');
                          // Re-trigger save after confirming
                          setTimeout(() => { document.querySelector('[data-testid="settings-save-button"]')?.click(); }, 100);
                        }} style={{ height: '36px', padding: '0 16px', borderRadius: '8px', border: 'none', background: '#DC2626', fontSize: '13px', fontWeight: '600', color: 'white', cursor: 'pointer' }}>Yes, Change Subdomain</button>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginTop: '16px' }}>
                  <ImageUpload
                    label="Company Logo"
                    hint={errors.logo ? undefined : "PNG or SVG, 320x88px recommended. Transparent background preferred."}
                    error={errors.logo}
                    fieldName="logo"
                    required
                    missingType={missing.logo}
                    currentUrl={brand?.logo_filename ? `https://static.quoteplicity.com${brand.logo_filename}` : null}
                    onUploaded={(url, filename) => updateBrand('logo_filename', filename)}
                    onRemoved={() => updateBrand('logo_filename', null)}
                    uploadFileFn={uploadFileFn}
                    brandId={brand?.id || brand?._id || brand?.brand_id}
                  />
                  <ImageUpload
                    label="Favicon"
                    hint="Square image, 32x32px or 64x64px. ICO, PNG, or SVG."
                    fieldName="favicon"
                    recommended
                    missingType={missing.favicon}
                    currentUrl={brand?.favicon_filename ? `https://static.quoteplicity.com${brand.favicon_filename}` : null}
                    onUploaded={(url, filename) => updateBrand('favicon_filename', filename)}
                    onRemoved={() => updateBrand('favicon_filename', null)}
                    uploadFileFn={uploadFileFn}
                    brandId={brand?.id || brand?._id || brand?.brand_id}
                  />
                  <ImageUpload
                    label="Default Agent Photo"
                    hint={errors.agent_photo ? undefined : "Upload any image — you'll crop it to a square. If left blank, the favicon or logo will be used."}
                    error={errors.agent_photo}
                    fieldName="agent_photo"
                    recommended
                    missingType={missing.agent_photo}
                    cropAspect={1}
                    currentUrl={brand?.details?.agent?.filename ? `https://static.quoteplicity.com${brand.details.agent.filename}` : null}
                    fallbackUrl={brand?.favicon_filename ? `https://static.quoteplicity.com${brand.favicon_filename}` : brand?.logo_filename ? `https://static.quoteplicity.com${brand.logo_filename}` : null}
                    onUploaded={(url, filename) => updateBrand('details.agent.filename', filename)}
                    onRemoved={() => updateBrand('details.agent.filename', null)}
                    uploadFileFn={uploadFileFn}
                    brandId={brand?.id || brand?._id || brand?.brand_id}
                  />
                </div>

                {/* Brand Colors */}
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#334155', marginTop: '20px' }}>Website Color Theme</div>
                <div style={{ fontSize: '12px', color: '#64748B', marginTop: '4px', marginBottom: '16px' }}>Used in the design of widgets, buttons, landing pages, and all other website assets</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>
                  <ColorPickerField label="Primary color" hint="The main brand color used for headers, navigation, and primary text." value={brand?.details?.theme?.primary} defaultColor="#1E3A5F" onApply={v => updateBrand('details.theme.primary', v)} />
                  <ColorPickerField label="Secondary color" hint="A supporting color for links, interactive elements, and secondary buttons." value={brand?.details?.theme?.secondary} defaultColor="#58A3FF" onApply={v => updateBrand('details.theme.secondary', v)} />
                  <ColorPickerField label="Accent color" hint="Used for highlights, success states, and trust indicators." value={brand?.details?.theme?.accent} defaultColor="#e92027" onApply={v => updateBrand('details.theme.accent', v)} />
                  <ColorPickerField label="Default call to action color" hint="The standard color for all call-to-action buttons across your quote pages." value={brand?.details?.theme?.cta} defaultColor="#FF7F22" onApply={v => updateBrand('details.theme.cta', v)} />
                </div>
              </div>
              </div>
            </div>
          )}

          {/* ── QUOTER & FUNNEL ── */}
          {config.section === 'quoter' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
              <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#334155' }}>Custom Quoter Domain</div>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'linear-gradient(135deg, #FFF8E1, #FFF3C4)', border: '1px solid #F59E0B', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: '600', color: '#B45309' }}>
                  <i className="fas fa-trophy" style={{ fontSize: '10px', color: '#F59E0B' }}></i>
                  Professional Plan
                </span>
                {(() => {
                  const planId = brand?.plan_id || brand?.brand_plan_id;
                  const isRestricted = planId === 1 || planId === 7 || planId === '1' || planId === '7';
                  return !isRestricted ? <span style={{ fontSize: '11px', fontWeight: '500', color: '#10c0a5' }}>(recommended)</span> : null;
                })()}
              </div>
              <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 6px', lineHeight: '1.5' }}>
                Use a whitelabeled domain instead of your Quoteplicity subdomain.
              </p>
              <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 6px', lineHeight: '1.5' }}>
                Before starting, create a CNAME DNS record pointing your whitelabeled domain to{' '}
                <code style={{ background: '#F1F5F9', padding: '2px 6px', borderRadius: '3px', fontSize: '12px', fontFamily: 'monospace', color: '#334155' }}>cname.quoteplicity.com</code>.
              </p>
              <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 16px', lineHeight: '1.5' }}>
                See <a href="https://support.quoteplicity.com" target="_blank" rel="noopener noreferrer" style={{ color: '#2D87F0', textDecoration: 'none', fontWeight: '500' }}>Knowledge Base</a> for further details.
              </p>
              {(() => {
                const planId = brand?.plan_id || brand?.brand_plan_id;
                const isRestricted = planId === 1 || planId === 7 || planId === '1' || planId === '7';
                const canApply = !isRestricted && domainInput.trim().length > 0 && domainState !== 1;

                const handleApplyDomain = () => {
                  if (!canApply) return;
                  setDomainState(1);
                  setDomainMessage('Checking domain...');
                  sendToParent({ type: 'apply_quoter_domain', brand_id: brand.id || brand._id || brand.brand_id, domain: domainInput.trim() });
                };

                return (
                  <div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        {missing.custom_domain === 'recommended' ? (
                          <div data-testid="missing-recommended-outline" style={{ position: 'relative', border: '2px solid #10c0a5', borderRadius: '10px', padding: '2px', background: '#F0FDFA' }}>
                            <span aria-hidden="false" style={{ position: 'absolute', top: '-9px', right: '8px', background: '#fff', padding: '0 4px', display: 'inline-flex', alignItems: 'center', lineHeight: 1, pointerEvents: 'none', zIndex: 1 }}>
                              <WarningTriangle size={14} title="Recommended field missing" />
                            </span>
                            <input
                              data-testid="custom-domain-input"
                              className="input-field"
                              type="text"
                              value={domainInput}
                              onChange={e => { setDomainInput(e.target.value); if (domainState === 2 || domainState === 3) { setDomainState(0); setDomainMessage(''); } }}
                              placeholder="quotes.yourbusiness.com"
                              disabled={isRestricted || domainState === 1}
                              style={isRestricted ? { backgroundColor: '#F1F5F9', color: '#94A3B8', cursor: 'not-allowed' } : {}}
                            />
                          </div>
                        ) : (
                          <input
                            data-testid="custom-domain-input"
                            className="input-field"
                            type="text"
                            value={domainInput}
                            onChange={e => { setDomainInput(e.target.value); if (domainState === 2 || domainState === 3) { setDomainState(0); setDomainMessage(''); } }}
                            placeholder="quotes.yourbusiness.com"
                            disabled={isRestricted || domainState === 1}
                            style={isRestricted ? { backgroundColor: '#F1F5F9', color: '#94A3B8', cursor: 'not-allowed' } : {}}
                          />
                        )}
                      </div>
                      {!isRestricted && (
                        <button
                          data-testid="apply-domain-button"
                          onClick={handleApplyDomain}
                          disabled={!canApply}
                          style={{
                            padding: '8px 20px', borderRadius: '6px', border: 'none', fontWeight: '600', fontSize: '13px',
                            cursor: canApply ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', height: '40px',
                            background: canApply ? '#2D87F0' : '#CBD5E1', color: '#fff',
                            transition: 'background 0.15s',
                          }}
                        >
                          {domainState === 1 ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: '6px' }}></i>Applying...</> : 'Apply'}
                        </button>
                      )}
                    </div>
                    {isRestricted && (
                      <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <i className="fas fa-lock" style={{ fontSize: '11px' }}></i>
                        Available on Professional and Agency plans
                      </div>
                    )}
                    {domainState === 1 && domainMessage && (
                      <div data-testid="domain-status-applying" style={{ fontSize: '12px', color: '#2D87F0', background: '#EFF6FF', borderRadius: '6px', padding: '8px 12px', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #BFDBFE' }}>
                        <i className="fas fa-circle-notch fa-spin" style={{ fontSize: '11px' }}></i>
                        <span>{domainMessage}</span>
                      </div>
                    )}
                    {domainState === 2 && (
                      <div data-testid="domain-status-complete" style={{ fontSize: '12px', color: '#059669', background: '#ECFDF5', borderRadius: '6px', padding: '8px 12px', marginTop: '8px', display: 'flex', alignItems: 'flex-start', gap: '6px', border: '1px solid #A7F3D0' }}>
                        <i className="fas fa-circle-check" style={{ fontSize: '12px', marginTop: '1px' }}></i>
                        <span dangerouslySetInnerHTML={{ __html: domainMessage || 'Domain applied successfully.' }} />
                      </div>
                    )}
                    {domainState === 3 && (
                      <div data-testid="domain-status-failed" style={{ fontSize: '12px', color: '#DC2626', background: '#FEF2F2', borderRadius: '6px', padding: '8px 12px', marginTop: '8px', display: 'flex', alignItems: 'flex-start', gap: '6px', border: '1px solid #FECACA' }}>
                        <i className="fas fa-circle-xmark" style={{ fontSize: '12px', marginTop: '1px' }}></i>
                        <span dangerouslySetInnerHTML={{ __html: domainMessage || `It looks like it's taking a while to get your connection ready. Please give us a few minutes, and then try to visit <a href="https://${domainInput.trim()}" target="_blank" style="color:#2D87F0;text-decoration:underline">${domainInput.trim()}</a> in your browser to confirm it was set up correctly. If you are having trouble, you can <a href="https://support.quoteplicity.com" target="_blank" style="color:#2D87F0;text-decoration:underline">contact us.</a>` }} />
                      </div>
                    )}
                    {domainState === 0 && !isRestricted && domainInput.trim() && (
                      <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <i className="fas fa-arrow-right" style={{ fontSize: '10px' }}></i>
                        Click Apply to connect your custom domain
                      </div>
                    )}
                  </div>
                );
              })()}
              </div>
              <hr style={{ gridColumn: '1 / -1', borderColor: '#DDE7F1', margin: '16px 0 24px' }} />
              <div style={{ gridColumn: '1 / -1', fontWeight: '600', fontSize: '14px', color: '#334155', marginBottom: '16px' }}>Funnel &amp; Quoter</div>
              <FormField label="Require lead info to see results"
                hint="Highly recommended. Requiring name, email, and phone before showing results captures contact details before the prospect leaves. Disabling this shows results without any lead capture.">
                <select className="input-field" value={brand?.details?.use_blocker_modal || '1'} onChange={e => updateBrand('details.use_blocker_modal', e.target.value)} style={{ appearance: 'none', cursor: 'pointer' }}>
                  <option value="1">Yes — require contact info (Recommended)</option>
                  <option value="0">No — show results without capturing info</option>
                </select>
              </FormField>
              <FormField label="Require phone verification to see results"
                hint="Sends a 6-digit SMS code to verify the prospect's phone number before showing quotes. Reduces fake leads but adds friction. Only enable if lead quality is a significant concern.">
                <select className="input-field" value={brand?.details?.phone_verification || ''} onChange={e => updateBrand('details.phone_verification', e.target.value)} style={{ appearance: 'none', cursor: 'pointer' }}>
                  <option value="">No — skip verification (Default)</option>
                  <option value="1">Yes — require SMS verification</option>
                </select>
              </FormField>
              <FormField label="Results page CTA button text"
                hint="The label on the main call-to-action button on the quote results page. Keep it action-oriented. Default is 'Continue'. Examples: 'Apply Now', 'Get Coverage', 'Proceed'.">
                <input className="input-field" type="text" value={brand?.details?.results_page?.cta_text || ''} onChange={e => updateBrand('details.results_page.cta_text', e.target.value)} placeholder="Continue" />
              </FormField>
              <div style={{ gridColumn: '1 / -1' }}>
                <hr style={{ borderColor: '#DDE7F1', margin: '8px 0 24px' }} />
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#334155', marginBottom: '4px' }}>Trust &amp; Social Proof</div>
                <p style={{ fontSize: '12px', color: '#64748B', margin: '0 0 16px' }}>Only two of these can display on the results page.</p>
              </div>
              <FormField label="BBB profile URL"
                hint="Link to your Better Business Bureau listing. Displays a trust badge on your quote pages to reassure prospects about your credibility.">
                <input className="input-field" type="url" value={brand?.details?.bbb_link || ''} onChange={e => updateBrand('details.bbb_link', e.target.value)} placeholder="https://www.bbb.org/your-business" />
              </FormField>
              <FormField label="Google Reviews URL"
                hint="Direct link to your Google Business profile reviews. Showing star ratings increases trust and conversion rates.">
                <input className="input-field" type="url" value={brand?.details?.google_reviews_link || ''} onChange={e => updateBrand('details.google_reviews_link', e.target.value)} placeholder="https://g.page/your-business/review" />
              </FormField>
              <FormField label="Trustpilot URL"
                hint="Your Trustpilot business profile URL. Displayed alongside BBB and Google ratings on quote pages.">
                <input className="input-field" type="url" value={brand?.details?.trustpilot_link || ''} onChange={e => updateBrand('details.trustpilot_link', e.target.value)} placeholder="https://www.trustpilot.com/review/youragency.com" />
              </FormField>

              <div style={{ gridColumn: '1 / -1' }}>
                <hr style={{ borderColor: '#DDE7F1', margin: '8px 0 24px' }} />
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#334155', marginBottom: '16px' }}>Application &amp; Jumpstart</div>
              </div>

              <FormField label="Skip Apply page"
                hint="Choose whether to show the Apply page after results, or skip directly to the Jumpstart page.">
                <select className="input-field" value={brand?.details?.apply_page?.skip_to_jumpstart || '0'} onChange={e => updateBrand('details.apply_page.skip_to_jumpstart', e.target.value)} style={{ appearance: 'none', cursor: 'pointer' }}>
                  <option value="0">No — Show Apply Page After Results</option>
                  <option value="1">Yes — Show Jumpstart After Results</option>
                </select>
              </FormField>

              <FormField label="Offer Jumpstart / contact options"
                hint="When enabled, users are presented with Jumpstart and contact options on the Application Confirmation page.">
                <select className="input-field" value={brand?.details?.apply_page?.simple_submit ?? '1'} onChange={e => updateBrand('details.apply_page.simple_submit', e.target.value)} style={{ appearance: 'none', cursor: 'pointer' }}>
                  <option value="1">No (Default)</option>
                  <option value="0">Yes</option>
                </select>
              </FormField>

              {brand?.details?.apply_page?.simple_submit !== '1' && (
                <>
                  <FormField label="Customize Jumpstart CTA"
                    hint="Override the default Jumpstart call-to-action with your own custom title, description, and button text.">
                    <select className="input-field" value={brand?.details?.apply_page?.custom_jumpstart_cta?.active || '0'} onChange={e => updateBrand('details.apply_page.custom_jumpstart_cta.active', e.target.value)} style={{ appearance: 'none', cursor: 'pointer' }}>
                      <option value="0">No — use default Jumpstart CTA</option>
                      <option value="1">Yes — customize the Jumpstart CTA</option>
                    </select>
                  </FormField>

                  {brand?.details?.apply_page?.custom_jumpstart_cta?.active === '1' && (
                    <>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <hr style={{ borderColor: '#DDE7F1', margin: '8px 0 20px' }} />
                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#334155', marginBottom: '16px' }}>Custom Jumpstart CTA</div>
                      </div>
                      <FormField label="CTA Title" hint="The headline shown on the Jumpstart call-to-action card. e.g. 'Get a jump-start'">
                        <input className="input-field" type="text" value={brand?.details?.apply_page?.custom_jumpstart_cta?.title || ''} onChange={e => updateBrand('details.apply_page.custom_jumpstart_cta.title', e.target.value)} placeholder="Get a jump-start" />
                      </FormField>
                      <FormField label="CTA Description" hint="Supporting text below the CTA title.">
                        <input className="input-field" type="text" value={brand?.details?.apply_page?.custom_jumpstart_cta?.description || ''} onChange={e => updateBrand('details.apply_page.custom_jumpstart_cta.description', e.target.value)} placeholder="Speed up your application and spend less time on the phone." />
                      </FormField>
                      <FormField label="CTA Button text" hint="Label for the Jumpstart button.">
                        <input className="input-field" type="text" value={brand?.details?.apply_page?.custom_jumpstart_cta?.cta_text || ''} onChange={e => updateBrand('details.apply_page.custom_jumpstart_cta.cta_text', e.target.value)} placeholder="Continue" />
                      </FormField>
                      <FormField label="CTA Icon character" hint="Optionally replace the default icon with a single text character (emoji or letter). Leave blank to use the default icon.">
                        <input className="input-field" type="text" value={brand?.details?.apply_page?.custom_jumpstart_cta?.icon || ''} onChange={e => updateBrand('details.apply_page.custom_jumpstart_cta.icon', e.target.value)} placeholder="e.g. 🚀" maxLength={2} style={{ maxWidth: '80px' }} />
                      </FormField>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── DISCLAIMERS & LEGAL ── */}
          {config.section === 'legal' && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>

              {/* Legal Links — top */}
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#334155', marginBottom: '16px' }}>Legal Links</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
                <FormField label="Privacy policy URL" required
                  error={errors.privacy_policy}
                  missingType={missing.privacy_policy}
                  hint="Link to your privacy policy page. Required for TCPA compliance and strongly recommended for all lead capture forms. Shown in your quote footer.">
                  <input className="input-field" type="url" value={brand?.details?.legal_links?.privacy_policy || ''}
                    onChange={e => { updateBrand('details.legal_links.privacy_policy', e.target.value); setErrors(p => ({...p, privacy_policy: ''})); }}
                    placeholder="https://youragency.com/privacy-policy"
                    style={errors.privacy_policy ? { borderColor: '#DC2626' } : {}} />
                </FormField>
                <FormField label="Licenses URL"
                  hint="Link to a page listing your state insurance license numbers and/or NPN. Builds credibility and may be required by certain states.">
                  <input className="input-field" type="url" value={brand?.details?.legal_links?.licenses || ''} onChange={e => updateBrand('details.legal_links.licenses', e.target.value)} placeholder="https://youragency.com/licenses" />
                </FormField>
              </div>

              <hr style={{ borderColor: '#DDE7F1', margin: '8px 0 24px' }} />
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#334155', marginBottom: '16px' }}>Business Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
                <FormField label="Legal business name" recommended
                  missingType={missing.title_legal}
                  hint="Your full registered business name as it appears on legal documents and state filings. This name will be auto-populated into your custom results and custom apply page disclaimers.">
                  <input className="input-field" type="text" value={brand?.title_legal || ''} onChange={e => updateBrand('title_legal', e.target.value)} placeholder="e.g. Smith Life Insurance LLC" />
                </FormField>
                <FormField label="Business email address" recommended
                  missingType={missing.email_internal}
                  hint="Used for internal notifications, lead alerts, and billing. Not visible to customers.">
                  <input className="input-field" type="email" value={brand?.email_internal || ''} onChange={e => updateBrand('email_internal', e.target.value)} placeholder="admin@youragency.com" />
                </FormField>
                <FormField label="Account phone number" recommended
                  error={errors.phone_internal}
                  missingType={missing.phone_internal}
                  hint="Your direct line for platform notifications and internal communications. Not shown to customers. This number will be auto-populated into your custom results and custom apply page disclaimers.">
                  <input className="input-field" type="tel" value={brand?.phone_internal || ''} onChange={e => {
                    let digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    let formatted = '';
                    if (digits.length > 0) formatted += '(' + digits.slice(0, 3);
                    if (digits.length >= 3) formatted += ') ';
                    if (digits.length > 3) formatted += digits.slice(3, 6);
                    if (digits.length >= 6) formatted += '-';
                    if (digits.length > 6) formatted += digits.slice(6, 10);
                    updateBrand('phone_internal', formatted);
                    setErrors(p => ({...p, phone_internal: ''}));
                  }}
                  onBlur={e => {
                    const digits = (e.target.value || '').replace(/\D/g, '');
                    if (digits.length > 0 && digits.length < 10) {
                      setErrors(p => ({...p, phone_internal: 'Phone number must be 10 digits: (xxx) xxx-xxxx'}));
                    } else if (digits.length === 10) {
                      const isSequential = '1234567890'.includes(digits) || '0987654321'.includes(digits);
                      const isRepeated = /^(.)\1{9}$/.test(digits);
                      if (isSequential || isRepeated) {
                        setErrors(p => ({...p, phone_internal: 'Please enter a valid phone number (no sequential or repeated digits)'}));
                      }
                    }
                  }}
                  placeholder="(555) 123-4567" style={errors.phone_internal ? { borderColor: '#DC2626' } : {}} />
                </FormField>
              </div>

              <hr style={{ borderColor: '#DDE7F1', margin: '8px 0 24px' }} />

              <FormField label="Custom results disclaimer"
                hint="Displayed below quote results. Uses TCPA-compliant consent language. The agency name and phone number are auto-populated from your settings.">
                <textarea className="input-field" style={{ height: 'auto', minHeight: '120px', padding: '8px 12px', resize: 'vertical', fontSize: '13px', lineHeight: '1.5' }} value={brand?.details?.results_disclaimer || ''} onChange={e => updateBrand('details.results_disclaimer', e.target.value)} />
                {brand?.details?.results_disclaimer !== getDefaultResultsDisclaimer(brand) && (
                  <button type="button" onClick={() => updateBrand('details.results_disclaimer', getDefaultResultsDisclaimer(brand))} style={{ marginTop: '8px', background: '#EFF6FF', border: '1px solid #BFDBFE', fontSize: '13px', fontWeight: '500', color: '#2563EB', cursor: 'pointer', padding: '6px 14px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', gap: '6px', transition: 'background 150ms' }} onMouseEnter={e => e.currentTarget.style.background = '#DBEAFE'} onMouseLeave={e => e.currentTarget.style.background = '#EFF6FF'}>
                    <i className="fas fa-rotate-left" style={{ fontSize: '12px' }}></i>
                    Update default disclaimer with Business Details
                  </button>
                )}
              </FormField>
              <FormField label="Custom footer language"
                hint="Legal boilerplate shown in the footer across all quote pages. Includes rate accuracy language.">
                <textarea className="input-field" style={{ height: 'auto', minHeight: '100px', padding: '8px 12px', resize: 'vertical', fontSize: '13px', lineHeight: '1.5' }} value={brand?.details?.legal_disclaimer || ''} onChange={e => updateBrand('details.legal_disclaimer', e.target.value)} />
                {brand?.details?.legal_disclaimer !== 'Rates are provided directly by the insurance companies, and we make every effort to keep them up to date. However, pricing can change frequently, and the results shown should be considered estimates. Your actual premium may vary based on factors such as age, health, medications, and underwriting.' && (
                  <button type="button" onClick={() => updateBrand('details.legal_disclaimer', 'Rates are provided directly by the insurance companies, and we make every effort to keep them up to date. However, pricing can change frequently, and the results shown should be considered estimates. Your actual premium may vary based on factors such as age, health, medications, and underwriting.')} style={{ marginTop: '8px', background: '#EFF6FF', border: '1px solid #BFDBFE', fontSize: '13px', fontWeight: '500', color: '#2563EB', cursor: 'pointer', padding: '6px 14px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', gap: '6px', transition: 'background 150ms' }} onMouseEnter={e => e.currentTarget.style.background = '#DBEAFE'} onMouseLeave={e => e.currentTarget.style.background = '#EFF6FF'}>
                    <i className="fas fa-rotate-left" style={{ fontSize: '12px' }}></i>
                    Update default disclaimer with Business Details
                  </button>
                )}
              </FormField>
              <FormField label="Custom apply page disclaimer"
                hint="Consent language displayed on the apply/submit page. Includes TCPA and FCC compliance language.">
                <textarea className="input-field" style={{ height: 'auto', minHeight: '100px', padding: '8px 12px', resize: 'vertical', fontSize: '13px', lineHeight: '1.5' }} value={brand?.details?.apply_disclaimer || ''} onChange={e => updateBrand('details.apply_disclaimer', e.target.value)} />
                {brand?.details?.apply_disclaimer !== getDefaultApplyDisclaimer(brand) && (
                  <button type="button" onClick={() => updateBrand('details.apply_disclaimer', getDefaultApplyDisclaimer(brand))} style={{ marginTop: '8px', background: '#EFF6FF', border: '1px solid #BFDBFE', fontSize: '13px', fontWeight: '500', color: '#2563EB', cursor: 'pointer', padding: '6px 14px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', gap: '6px', transition: 'background 150ms' }} onMouseEnter={e => e.currentTarget.style.background = '#DBEAFE'} onMouseLeave={e => e.currentTarget.style.background = '#EFF6FF'}>
                    <i className="fas fa-rotate-left" style={{ fontSize: '12px' }}></i>
                    Update default disclaimer with Business Details
                  </button>
                )}
              </FormField>
              <FormField label="Disclaimer acknowledgment checkbox"
                hint="Adds a checkbox to your quote form requiring prospects to acknowledge your terms. 'Required, pre-checked' is the least friction while still providing documented consent.">
                <select className="input-field" value={brand?.details?.disclaimer_checkbox || ''} onChange={e => updateBrand('details.disclaimer_checkbox', e.target.value)} style={{ appearance: 'none', cursor: 'pointer' }}>
                  <option value="">None — no checkbox shown (Default)</option>
                  <option value="optional">Optional — shown but not required</option>
                  <option value="show">Required — must check before proceeding</option>
                  <option value="prechecked">Required, pre-checked — checked by default</option>
                </select>
              </FormField>
            </div>
          )}

          {/* ── TRACKING & ANALYTICS ── */}
          {config.section === 'tracking' && (
            <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
              <div style={{ gridColumn: '1 / -1', fontWeight: '600', fontSize: '14px', color: '#334155', marginBottom: '4px' }}>Google</div>
              <FormField label="Google Tag (GA4) ID" recommended
                missingType={missing.ga_id}
                hint="Your Google Analytics 4 Measurement ID. Starts with 'G-'. Found in GA4 under Admin → Data Streams → your stream. Tracks page views and quote funnel events.">
                <input className="input-field" type="text" value={brand?.details?.tracking?.ga_id || ''} onChange={e => updateBrand('details.tracking.ga_id', e.target.value)} placeholder="G-XXXXXXXXXX" />
              </FormField>
              <FormField label="Google Ads conversion ID"
                hint="The numeric conversion ID from your Google Ads account. Found in Ads → Tools → Conversions → your conversion action. Required for Google Ads lead tracking.">
                <input className="input-field" type="text" value={brand?.details?.tracking?.adwords_conversion_id || ''} onChange={e => updateBrand('details.tracking.adwords_conversion_id', e.target.value)} placeholder="e.g. 123456789" />
              </FormField>
              <FormField label="Google Ads conversion label"
                hint="The alphanumeric label paired with your conversion ID. Found alongside your conversion ID in Google Ads. Both are needed for accurate conversion tracking.">
                <input className="input-field" type="text" value={brand?.details?.tracking?.adwords_conversion_label || ''} onChange={e => updateBrand('details.tracking.adwords_conversion_label', e.target.value)} placeholder="e.g. AbCdEfGhIjK" />
              </FormField>
              <div style={{ gridColumn: '1 / -1', fontWeight: '600', fontSize: '14px', color: '#334155', margin: '8px 0 4px', paddingTop: '16px', borderTop: '1px solid #DDE7F1' }}>Meta / Facebook</div>
              <FormField label="Facebook Pixel ID / Dataset ID"
                hint="Your 15–16 digit Meta Pixel ID. Found in Meta Business Suite → Events Manager → your Pixel. Tracks quote starts, lead submissions, and page views for ad optimization.">
                <input className="input-field" type="text" value={brand?.details?.tracking?.fb_pixel_id || ''} onChange={e => updateBrand('details.tracking.fb_pixel_id', e.target.value)} placeholder="e.g. 1234567890123456" />
              </FormField>
              <FormField label="Facebook Conversion API token"
                hint="Server-side access token for the Meta Conversions API. Enables more reliable lead tracking that isn't blocked by ad blockers or iOS privacy restrictions. Found in Events Manager → Settings → Conversions API.">
                <input className="input-field" type="text" value={brand?.details?.tracking?.fb_conversion_token || ''} onChange={e => updateBrand('details.tracking.fb_conversion_token', e.target.value)} placeholder="EAAxxxxxxxxxxxxxxx..." />
              </FormField>
              <div style={{ gridColumn: '1 / -1', fontWeight: '600', fontSize: '14px', color: '#334155', margin: '8px 0 4px', paddingTop: '16px', borderTop: '1px solid #DDE7F1' }}>Microsoft</div>
              <FormField label="Bing Ads / Microsoft UET tag ID"
                hint="Your Microsoft Advertising Universal Event Tracking (UET) tag ID. Found in Microsoft Ads → Tools → UET tags. Enables conversion tracking and remarketing for Bing/Edge traffic.">
                <input className="input-field" type="text" value={brand?.details?.tracking?.bing_site_id || ''} onChange={e => updateBrand('details.tracking.bing_site_id', e.target.value)} placeholder="e.g. 12345678" />
              </FormField>
              <div style={{ gridColumn: '1 / -1', fontWeight: '600', fontSize: '14px', color: '#334155', margin: '8px 0 4px', paddingTop: '16px', borderTop: '1px solid #DDE7F1' }}>Jornaya / Verisk</div>
              <FormField label="Jornaya campaign key"
                hint="Your Jornaya LeadiD campaign key. Found in your Jornaya dashboard under Campaigns. Generates a unique LeadiD token for each visitor to prove TCPA-compliant consent and lead authenticity.">
                <input className="input-field" type="text" value={brand?.details?.tracking?.jornaya_campaign_key || ''} onChange={e => updateBrand('details.tracking.jornaya_campaign_key', e.target.value)} placeholder="e.g. xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
              </FormField>
            </div>
            <div>
              {(() => {
                const cs = brand?.details?.custom_scripts;
                const displayValue = cs?.requested_value ?? cs?.current_value ?? '';
                // Use last-saved state for notices — only changes on save, not while typing
                const saved = savedScriptsRef.current;
                const savedStatus = saved?.status ?? 0;
                const savedHasContent = (saved?.current_value ?? '').trim() !== '' || (saved?.requested_value ?? '').trim() !== '';
                const statusConfig = savedHasContent ? {
                  1: { label: 'Active', color: '#059669', bg: '#ECFDF5', icon: 'fa-circle-check' },
                  2: { label: 'Pending Review', color: '#D97706', bg: '#FFFBEB', icon: 'fa-clock' },
                  3: { label: 'Rejected', color: '#DC2626', bg: '#FEF2F2', icon: 'fa-circle-xmark' },
                }[savedStatus] : null;

                return (<>
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#334155', margin: '8px 0 4px', paddingTop: '16px', borderTop: '1px solid #DDE7F1', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    Custom Scripting
                    {statusConfig && (
                      <span data-testid="custom-scripts-status-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: '600', color: statusConfig.color, background: statusConfig.bg, padding: '2px 9px', borderRadius: '20px' }}>
                        <i className={`fas ${statusConfig.icon}`} style={{ fontSize: '10px' }}></i>
                        {statusConfig.label}
                      </span>
                    )}
                  </div>
                  <FormField label={<>Custom scripting requests <span style={{ color: '#DC2626', fontWeight: '600' }}>(Approval Required)</span></>}
                    hint="Paste any custom tracking scripts, conversion pixels, or third-party code snippets you need added to your quote pages. All requests are reviewed and approved by our technical staff before deployment.">
                    <textarea data-testid="custom-scripts-textarea" className="input-field" style={{ height: 'auto', minHeight: '120px', padding: '8px 12px', resize: 'vertical', fontFamily: 'monospace', fontSize: '13px', width: '100%' }}
                      value={displayValue}
                      onChange={e => {
                        const val = e.target.value;
                        const prev = brand?.details?.custom_scripts;
                        updateBrand('details.custom_scripts', {
                          status: prev?.status ?? 0,
                          current_value: prev?.current_value ?? '',
                          requested_value: val,
                        });
                      }}
                      placeholder="Place your custom scripts here (e.g. chatbots, Clarity screen recording, pop-ups, calendars)" />
                    {savedStatus === 2 && savedHasContent && (
                      <div data-testid="custom-scripts-pending-note" style={{ fontSize: '12px', color: '#D97706', background: '#FFFBEB', borderRadius: '6px', padding: '8px 12px', marginTop: '6px', display: 'flex', alignItems: 'flex-start', gap: '6px', border: '1px solid #FDE68A' }}>
                        <i className="fas fa-clock" style={{ fontSize: '12px', marginTop: '1px' }}></i>
                        <span>Your script is being reviewed and will be automatically enabled after it has been approved.</span>
                      </div>
                    )}
                    {savedStatus === 3 && savedHasContent && (
                      <div data-testid="custom-scripts-rejected-note" style={{ fontSize: '12px', color: '#DC2626', background: '#FEF2F2', borderRadius: '6px', padding: '8px 12px', marginTop: '6px', display: 'flex', alignItems: 'flex-start', gap: '6px', border: '1px solid #FECACA' }}>
                        <i className="fas fa-circle-xmark" style={{ fontSize: '12px', marginTop: '1px' }}></i>
                        <span>Your script request was not approved. Please revise and resubmit.</span>
                      </div>
                    )}
                    {(!savedHasContent || (savedStatus !== 2 && savedStatus !== 3)) && (
                      <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <i className="fas fa-shield-halved" style={{ fontSize: '11px' }}></i>
                        Submissions are reviewed and approved by technical staff before being applied
                      </div>
                    )}
                  </FormField>
                </>);
              })()}
            </div>
            </>
          )}

        </div>{/* end form card */}
          </div>{/* end form column */}

        </div>{/* end layout */}
      </div>

      {/* Dirty save banner */}
      {isDirty && (
        <div className="save-banner" data-testid="dirty-save-banner">
          <div style={{ fontSize: '14px', color: '#334155', fontWeight: '500' }}>
            <i className="fas fa-circle-info mr-2 text-blue-400"></i>
            You have unsaved changes
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn-secondary" style={{ padding: '6px 16px', height: '36px' }} onClick={handleDiscard} data-testid="dirty-save-discard-button">Discard</button>
            <button className="btn-primary" style={{ padding: '6px 16px', height: '36px' }} onClick={handleSave} disabled={saving} data-testid="settings-save-button">
              {saving ? <><i className="fas fa-spinner fa-spin mr-1"></i>Saving...</> : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </ProtectedLayout>
  );
}
